import {
  applySnapshot,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { LeoRPCResult, LeoUUID } from "@surya-digital/leo-ts-types";
import { PreviewEmailRPC } from "@pulse/pulse-rpcs/lib/email-cycle/previewEmailRPC";
import { getAPIClient } from "../../networking/APIClient";
import {
  createEmailContentModel,
  EmailContentModel,
} from "../models/EmailContentModel";
import { usePreviewEmailRPCClientImpl } from "../rpcs/RPC";

const getInitialState = (): SnapshotIn<typeof PreviewEmailStore> => ({
  emailContent: createEmailContentModel(),
  loadingPreviewEmail: false,
  previewEmailRPCError: null,
});

export const PreviewEmailStore = types
  .model("PreviewEmailStore", {
    emailContent: EmailContentModel,
    loadingPreviewEmail: types.boolean,
    previewEmailRPCError: types.maybeNull(
      types.enumeration(
        "PreviewEmailRPCError",
        Object.values(PreviewEmailRPC.RPCError),
      ),
    ),
  })
  .actions((store) => ({
    previewEmail: flow(function* (emailId: string) {
      store.loadingPreviewEmail = true;
      store.previewEmailRPCError = null;

      try {
        const apiClient = getAPIClient(store);
        const request = new PreviewEmailRPC.Request(new LeoUUID(emailId));

        const result: LeoRPCResult<
          PreviewEmailRPC.Response,
          PreviewEmailRPC.Errors.Errors
        > = yield usePreviewEmailRPCClientImpl(apiClient).execute(request);

        if ("response" in result) {
          store.emailContent.subject.setName(
            result.response.emailContent.subject.name,
          );
          store.emailContent.body.setContent(
            result.response.emailContent.body.content,
          );
          return;
        }

        switch (result.error.code) {
          case PreviewEmailRPC.RPCError.InvalidEmailId:
          case PreviewEmailRPC.RPCError.UnexpectedError:
            store.previewEmailRPCError = result.error.code;
            break;
          default:
            result.error.code satisfies never;
        }
      } finally {
        store.loadingPreviewEmail = false;
      }
    }),
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createPreviewEmailStore = (): Instance<typeof PreviewEmailStore> =>
  PreviewEmailStore.create(getInitialState());
